import {
  Input,
  OnInit,
  inject,
  Component,
  ViewChild,
  OnDestroy,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  faInfoCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { NgpPopoverPlacement, NgpPopoverTrigger } from 'ng-primitives/popover';
import { TranslateService } from '@ngx-translate/core';

import { HelpService } from '../core/help.service';
import { HelpContentService } from '../core/help-content.service';

import {
  HELP_DEFAULTS,
  HelpContentState,
  DimensionValue,
} from '../core/help.models';

import { Observable, Subscription, filter, tap } from 'rxjs';

@Component({
  selector: 'app-help-popover',
  standalone: false,
  templateUrl: './help-popover.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './help-popover.component.scss',
})
export class HelpPopoverComponent implements OnInit, OnDestroy {
  @ViewChild('popoverTriggerRef') popoverTriggerRef?: NgpPopoverTrigger;

  @Input() helpKey!: string;

  @Input() anchorId?: string;

  @Input() placement: NgpPopoverPlacement = HELP_DEFAULTS.PLACEMENT;
  @Input() title?: string;

  @Input() width?: DimensionValue;
  @Input() height?: DimensionValue;
  @Input() minWidth?: DimensionValue;
  @Input() minHeight?: DimensionValue;
  @Input() maxWidth?: DimensionValue;
  @Input() maxHeight?: DimensionValue;

  contentState$!: Observable<HelpContentState>;

  faInfoCircle = faInfoCircle;
  faExclamationTriangle = faExclamationTriangle;

  titleId = `help-popover-title-${Math.random().toString(36).substr(2, 9)}`;
  displayTitle: string = '';

  private subscription?: Subscription;

  private destroyRef = inject(DestroyRef);
  private helpContentService = inject(HelpContentService);
  private helpService = inject(HelpService);
  private translate = inject(TranslateService);

  ngOnInit(): void {
    if (!this.helpKey) {
      console.warn('HelpPopoverComponent: helpKey is required');
      return;
    }

    // * Set title - use provided or translate default
    this.displayTitle =
      this.title || this.translate.instant('help.default-title');

    // * listen for programmatic close from HelpService
    this.helpService.activePopoverId$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          (activeId) =>
            activeId !== this.anchorId && this.popoverTriggerRef?.open(),
        ),
        tap(() => {
          // Another popover opened or popover was closed programmatically
          this.popoverTriggerRef?.hide();
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onPopoverOpenChange(isOpen: boolean): void {
    if (isOpen && !this.contentState$) {
      this.contentState$ = this.helpContentService.getContent(this.helpKey);
    }

    // * only call HelpService if anchorId exists (directive mode)
    if (this.anchorId) {
      if (isOpen) {
        this.helpService.openPopover(this.anchorId);
      } else if (this.helpService.getActivePopoverId() === this.anchorId) {
        this.helpService.closePopover();
      }
    }
  }

  get customStyles(): Record<string, string> | null {
    const styles: Record<string, string> = {};
    let hasCustomStyles = false;

    if (this.width) {
      styles['width'] = this.resolveDimension(this.width);
      hasCustomStyles = true;
    }
    if (this.height) {
      styles['height'] = this.resolveDimension(this.height);
      hasCustomStyles = true;
    }
    if (this.minWidth) {
      styles['min-width'] = this.resolveDimension(this.minWidth);
      hasCustomStyles = true;
    }
    if (this.minHeight) {
      styles['min-height'] = this.resolveDimension(this.minHeight);
      hasCustomStyles = true;
    }
    if (this.maxWidth) {
      styles['max-width'] = this.resolveDimension(this.maxWidth);
      hasCustomStyles = true;
    }
    if (this.maxHeight) {
      styles['max-height'] = this.resolveDimension(this.maxHeight);
      hasCustomStyles = true;
    }

    return hasCustomStyles ? styles : null;
  }

  private resolveDimension(value: DimensionValue): string {
    return typeof value === 'number' ? `${value}px` : value;
  }
}
